import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart, FREE_SHIP_THRESHOLD } from "@/context/CartContext";
import { money } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIP_THRESHOLD) * 100);

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
      />
      <aside
        className={`fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white z-50 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="cart-drawer"
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h3 className="font-heading font-bold uppercase tracking-widest text-lg">Your Bag ({items.length})</h3>
          <button onClick={() => setOpen(false)} data-testid="cart-close"><X size={22} /></button>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 bg-[#f9f9f7]">
          <p className="text-xs font-bold uppercase tracking-widest text-center mb-3">
            {remaining > 0 ? (
              <>You're <span className="text-sale">{money(remaining)}</span> away from FREE shipping</>
            ) : (
              <span className="text-sale">You unlocked FREE shipping! 🎾</span>
            )}
          </p>
          <div className="w-full bg-gray-200 h-1.5 overflow-hidden">
            <div className="h-full bg-sale transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4">
              <ShoppingBag size={48} className="text-gray-300" />
              <p className="text-sm uppercase tracking-widest font-bold text-gray-500">Your bag is empty</p>
              <button onClick={() => { setOpen(false); navigate("/shop/all"); }} className="bg-ink text-cream px-8 py-3 text-xs font-bold uppercase tracking-widest hover:bg-ink/80 transition-colors">
                Shop Now
              </button>
            </div>
          ) : (
            items.map((i) => (
              <div key={i.key} className="flex gap-4" data-testid={`cart-line-${i.key}`}>
                <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-gray-100" />
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between">
                    <h4 className="text-sm font-bold uppercase">{i.name}</h4>
                    <button onClick={() => removeItem(i.key)} data-testid={`cart-remove-${i.key}`}><X size={16} className="text-gray-400 hover:text-ink" /></button>
                  </div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Size: {i.size}</p>
                  <div className="mt-auto flex justify-between items-center">
                    <div className="flex items-center border border-gray-200">
                      <button onClick={() => updateQty(i.key, i.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200" data-testid={`cart-dec-${i.key}`}><Minus size={14} /></button>
                      <span className="px-3 text-sm font-bold">{i.quantity}</span>
                      <button onClick={() => updateQty(i.key, i.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200" data-testid={`cart-inc-${i.key}`}><Plus size={14} /></button>
                    </div>
                    <span className="font-bold text-sm">{money(i.price * i.quantity)}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-gray-100 flex flex-col gap-4">
            <div className="flex justify-between text-sm font-bold uppercase tracking-widest">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{money(subtotal)}</span>
            </div>
            <button onClick={checkout} className="w-full bg-ink text-cream py-4 font-bold uppercase tracking-widest hover:bg-ink/85 transition-colors flex justify-between px-6" data-testid="checkout-btn">
              <span>Checkout</span><span>{money(subtotal)}</span>
            </button>
          </div>
        )}
      </aside>
    </>
  );
};
